import React, { useEffect, useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ColorPicker } from '@/components/shared/ColorPicker'

export interface ProjectOption {
  id: number
  name: string
}

export interface ProjectAgent {
  agent: {
    id: number
    username: string
    agent_type: string
  }
  project_role: string
}

export type MemberRole = 'admin' | 'member' | 'no_access'

interface Props {
  open: boolean
  onClose: () => void
  projects: ProjectOption[]
  initialProjectId?: number
  currentAgentId: number
  submitUrl: string
}

const STEPS = ['Name', 'Members']

export function BoardWizardDialog({
  open,
  onClose,
  projects,
  initialProjectId,
  currentAgentId,
  submitUrl,
}: Props) {
  const [activeStep, setActiveStep] = useState(0)
  const [projectId, setProjectId] = useState<number | ''>('')
  const [name, setName] = useState('')
  const [color, setColor] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [projectAgents, setProjectAgents] = useState<ProjectAgent[]>([])
  const [roles, setRoles] = useState<Record<number, MemberRole>>({})
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loadingProject, setLoadingProject] = useState(false)

  // Reset on open
  useEffect(() => {
    if (open) {
      setActiveStep(0)
      setName('')
      setColor('')
      setSuggestions([])
      setProjectAgents([])
      setRoles({})
      setErrorMessage(null)
      const initial = initialProjectId ?? (projects.length === 1 ? projects[0].id : '')
      setProjectId(initial as number | '')
    }
  }, [open, initialProjectId, projects])

  // Fetch suggestions + project_agents whenever project changes
  useEffect(() => {
    if (!open) return
    if (projectId === '') {
      setSuggestions([])
      setProjectAgents([])
      setRoles({})
      return
    }

    let cancelled = false
    setLoadingProject(true)
    fetch(`/tasks/board_name_suggestions?project_id=${projectId}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : { suggestions: [], project_agents: [] }))
      .then((data) => {
        if (cancelled) return
        setSuggestions(data.suggestions || [])
        const agents: ProjectAgent[] = data.project_agents || []
        setProjectAgents(agents)
        const initialRoles: Record<number, MemberRole> = {}
        agents.forEach((pa) => {
          initialRoles[pa.agent.id] = pa.agent.id === currentAgentId ? 'admin' : 'no_access'
        })
        setRoles(initialRoles)
      })
      .catch(() => {
        if (cancelled) return
        setSuggestions([])
        setProjectAgents([])
      })
      .finally(() => {
        if (!cancelled) setLoadingProject(false)
      })

    return () => {
      cancelled = true
    }
  }, [projectId, open, currentAgentId])

  const canGoNext = useMemo(() => name.trim().length > 0 && projectId !== '', [name, projectId])

  function handleNext() {
    if (!canGoNext) return
    setActiveStep(1)
  }

  function handleBack() {
    setActiveStep(0)
  }

  function handleRoleChange(agentId: number, role: MemberRole) {
    setRoles((prev) => ({ ...prev, [agentId]: role }))
  }

  function handleSubmit() {
    if (submitting) return
    setSubmitting(true)
    setErrorMessage(null)

    const members = Object.entries(roles)
      .filter(([_id, role]) => role !== 'no_access')
      .map(([id, role]) => ({ agent_id: Number(id), role }))

    router.post(
      submitUrl,
      {
        board: {
          name: name.trim(),
          project_id: projectId,
          color: color || undefined,
        },
        members,
      },
      {
        onError: (errs) => {
          setSubmitting(false)
          const firstError = Object.values(errs)[0]
          setErrorMessage((firstError as string) || 'Could not create board')
        },
        onSuccess: () => {
          setSubmitting(false)
          onClose()
        },
        onFinish: () => {
          setSubmitting(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New board</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {errorMessage && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        {activeStep === 0 && (
          <Box>
            {projects.length > 1 && (
              <FormControl fullWidth sx={{ mb: 2.5 }}>
                <InputLabel>Project</InputLabel>
                <Select
                  value={projectId}
                  label="Project"
                  onChange={(e) => setProjectId(e.target.value as number)}
                  inputProps={{ 'data-testid': 'wizard-project-select' }}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Board name"
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
              slotProps={{ htmlInput: { 'data-testid': 'wizard-name-input', maxLength: 100 } }}
            />

            {suggestions.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Common board names
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {suggestions.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      onClick={() => setName(s)}
                      variant="outlined"
                      data-testid={`wizard-suggestion-${s}`}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Color (optional)
              </Typography>
              <ColorPicker value={color} onChange={setColor} />
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose access for each project member. The board creator is always an admin.
            </Typography>
            {loadingProject ? (
              <Typography variant="body2">Loading project members…</Typography>
            ) : projectAgents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No project members found.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell sx={{ width: 160 }}>Access</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectAgents.map((pa) => {
                      const isCreator = pa.agent.id === currentAgentId
                      return (
                        <TableRow key={pa.agent.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {pa.agent.username}
                              {isCreator && (
                                <Chip label="You" size="small" sx={{ ml: 1 }} variant="outlined" />
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={pa.agent.agent_type}
                              size="small"
                              variant="outlined"
                              color={pa.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={roles[pa.agent.id] || 'no_access'}
                                disabled={isCreator}
                                onChange={(e) =>
                                  handleRoleChange(pa.agent.id, e.target.value as MemberRole)
                                }
                                inputProps={{ 'data-testid': `wizard-role-${pa.agent.username}` }}
                              >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="member">Member</MenuItem>
                                <MenuItem value="no_access">No access</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {activeStep === 1 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        {activeStep === 0 ? (
          <Button onClick={handleNext} variant="contained" disabled={!canGoNext}>
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            data-testid="wizard-submit"
          >
            Create board
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BoardWizardDialog
